import { Lock } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import type { SubscriptionModalFormState } from './useSubscriptionModalForm';

const inputClass =
  'w-full rounded-lg border border-border-strong bg-muted px-3 py-2 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-tertiary/40 disabled:cursor-not-allowed disabled:opacity-70';
const labelClass = 'mb-1.5 block text-xs font-medium text-text-secondary';

function formatLimite(v: number | null): string {
  if (v == null) return '—';
  return v === 0 ? 'Ilimitado' : String(v);
}

interface LimitesSectionProps {
  form: SubscriptionModalFormState;
  planoNome: string | null;
  disabled?: boolean;
}

// Seção "② Limites" (modo Padrão, read-only, cadeado) / "② Limites do contrato"
// (modo Enterprise, editável, badge verde) — mapeiam pros `override_limite_*`.
export function LimitesSection({ form, planoNome, disabled }: LimitesSectionProps) {
  const isEnterprise = form.planoCard === 'enterprise';

  if (isEnterprise) {
    return (
      <div className="space-y-3">
        <SectionHeading
          num={2}
          tema="verde"
          title="Limites do contrato"
          trailing={
            <span className="inline-flex items-center rounded-full bg-tertiary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-tertiary">
              Editável
            </span>
          }
        />
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass} htmlFor="ent-limite-animais">
              Animais
            </label>
            <input
              id="ent-limite-animais"
              type="number"
              min={0}
              disabled={disabled}
              value={form.limites.animais}
              onChange={(e) => form.setLimites({ ...form.limites, animais: e.target.value })}
              className={inputClass}
              placeholder="ilimitado"
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="ent-limite-funcionarios">
              Funcionários
            </label>
            <input
              id="ent-limite-funcionarios"
              type="number"
              min={0}
              disabled={disabled}
              value={form.limites.funcionarios}
              onChange={(e) => form.setLimites({ ...form.limites, funcionarios: e.target.value })}
              className={inputClass}
              placeholder="ilimitado"
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="ent-limite-propriedades">
              Propriedades
            </label>
            <input
              id="ent-limite-propriedades"
              type="number"
              min={0}
              disabled={disabled}
              value={form.limites.propriedades}
              onChange={(e) => form.setLimites({ ...form.limites, propriedades: e.target.value })}
              className={inputClass}
              placeholder="ilimitado"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeading
        num={2}
        tema="azul"
        title="Limites"
        trailing={
          <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
            <Lock size={13} />
            definidos pelo plano {planoNome ?? '—'}
          </span>
        }
      />
      <div className="grid grid-cols-3 gap-3">
        <ReadOnlyLimite label="Animais" value={formatLimite(form.limitesPadrao.animais)} />
        <ReadOnlyLimite
          label="Funcionários"
          value={formatLimite(form.limitesPadrao.funcionarios)}
        />
        <ReadOnlyLimite
          label="Propriedades"
          value={formatLimite(form.limitesPadrao.propriedades)}
        />
      </div>
    </div>
  );
}

function ReadOnlyLimite({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="rounded-lg border border-border-strong bg-muted px-3 py-2 text-sm text-text-title">
        {value}
      </div>
    </div>
  );
}
